import useStitchedResult from 'hooks/useStitchedResult';
import { DEFAULT_PAGE_NUMBER } from 'utils/constants';
import { InputsMarketResultsQuery } from 'generated/graphql';

// The stitchers merge one page of results into the previous ones, so they walk
// exactly what the inputs market query selected rather than the generated
// schema types, which no query result can satisfy.
type InputsMarketSectionData = NonNullable<
  NonNullable<InputsMarketResultsQuery['inputsMarket']>['results']
>['sections'][number];
type InputsProductGroupData = InputsMarketSectionData['groups'][number];
type InputsProductData = InputsProductGroupData['products'][number];

const stitchProducts = (products?: InputsProductData[], productsNew?: InputsProductData[]) => {
  if (!products) {
    return productsNew;
  }
  if (!productsNew) {
    return products;
  }

  const indexesToSplice: Number[] = [];

  const data = products.map((product) => {
    const index = productsNew.findIndex((curr) => curr.productDetailsID === product.productDetailsID);

    if (index === -1) {
      return { ...product };
    }

    const newProduct = productsNew[index];
    indexesToSplice.push(index);

    return {
      ...product,
      ...newProduct,
    };
  });

  const dataToAdd = productsNew.filter((_, index) => !indexesToSplice.includes(index));

  return [...(data || []), ...(dataToAdd || [])];
};

const stitchGroups = (groups?: InputsProductGroupData[], groupsNew?: InputsProductGroupData[]) => {
  if (!groups) {
    return groupsNew;
  }
  if (!groupsNew) {
    return groups;
  }

  const indexesToSplice: Number[] = [];

  const data = groups.map((group) => {
    const index = groupsNew.findIndex((curr) => curr.ID === group.ID);

    if (index === -1) {
      return { ...group };
    }

    const newGroup = groupsNew[index];
    indexesToSplice.push(index);

    return {
      ...group,
      products: stitchProducts(group.products, newGroup.products) ?? [],
    };
  });

  const dataToAdd = groupsNew.filter((_, index) => !indexesToSplice.includes(index));

  return [...(data || []), ...(dataToAdd || [])];
};

const stitchSections = (
  sections?: InputsMarketSectionData[],
  sectionsNew?: InputsMarketSectionData[],
): InputsMarketSectionData[] => {
  if (!sections) {
    return sectionsNew ?? [];
  }
  if (!sectionsNew) {
    return sections;
  }

  const indexesToSplice: Number[] = [];

  const data = sections.map((section) => {
    const index = sectionsNew.findIndex((curr) => curr.ID === section.ID);

    if (index === -1) {
      return { ...section };
    }

    const marketSection = sectionsNew[index];
    indexesToSplice.push(index);

    return {
      ...section,
      groups: stitchGroups(section.groups, marketSection.groups) ?? [],
    };
  });

  const dataToAdd = sectionsNew.filter((_, index) => !indexesToSplice.includes(index));

  return [...(data ?? []), ...(dataToAdd ?? [])];
};

const stitchMarket = (
  marketResultQuery: InputsMarketResultsQuery | undefined,
  marketResultQueryNew: InputsMarketResultsQuery,
  isFirstPage = false,
): InputsMarketResultsQuery => {
  if (!marketResultQuery) {
    return marketResultQueryNew;
  }
  if (!marketResultQueryNew) {
    return marketResultQuery;
  }

  return {
    ...marketResultQuery,
    inputsMarket: {
      ...marketResultQuery.inputsMarket,
      results: {
        page: marketResultQueryNew.inputsMarket.results.page,
        sections:
          (marketResultQueryNew?.inputsMarket?.results?.sections?.length ?? 0) === 0 && isFirstPage
            ? []
            : stitchSections(
                marketResultQuery.inputsMarket.results.sections,
                marketResultQueryNew.inputsMarket.results.sections,
              ),
      },
    },
  };
};

const stitchInputsMarket = (previous: InputsMarketResultsQuery | undefined, next: InputsMarketResultsQuery) => {
  const isFirstPage = next?.inputsMarket?.results?.page?.page === DEFAULT_PAGE_NUMBER && !next?.inputsMarket;
  return isFirstPage ? next : stitchMarket(previous, next, isFirstPage);
};

const useStitchInputsMarket = (data: InputsMarketResultsQuery | undefined) => {
  const [marketQuery, setMarketQuery] = useStitchedResult(data, stitchInputsMarket);

  return { marketQuery, setMarketQuery };
};

export default useStitchInputsMarket;
